import { useMemo } from 'react';
import type { IDevExNormalizedRow } from '@skystone/shared';
import { devExApi } from '@/services/devex-api';
import { useApi } from '@/hooks/useApi';
import { DataTable, ErrorBanner, PageHeader, PageLoading } from '@/components/ui';
import type { ColumnDef } from '@/components/ui';
import { buildRawColumns, buildStandardEmptyMessage, buildStandardTblSubtitle, DEVEX_METRIC_SPECS, type IRawColumnSpec } from '@/lib/tbl-page-utils';
import { PageContainer } from '@/components/layout';

const DEFAULT_LIMIT = 500;
const DEFAULT_OFFSET = 0;

function getDevExNormalizedRowKey(row: IDevExNormalizedRow): string {
  return [
    row.quarter,
    row.sourceTable,
    row.sourceRowId ?? 'missing-source-row-id',
    row.teamRaw ?? 'missing-team-raw',
    row.teamNormalized ?? 'missing-team-normalized',
  ].join(':');
}

export function TblDevExQuarterlyNormalizedPage() {
  const { data, loading, error } = useApi(
    () => devExApi.listNormalized({ limit: DEFAULT_LIMIT, offset: DEFAULT_OFFSET }),
    [],
  );

  const columns = useMemo<ColumnDef<IDevExNormalizedRow>[]>(
    () => buildRawColumns<IDevExNormalizedRow>([
      { key: 'quarter', header: 'Quarter' },
      { key: 'sourceTable', header: 'Source Table' },
      { key: 'sourceRowId', header: 'Source Row ID' },
      { key: 'teamRaw', header: 'Team Raw' },
      { key: 'teamNormalized', header: 'Team Normalized' },
      { key: 'teamAliasNormalized', header: 'Team Alias Normalized' },
      { key: 'teamMappingStatus', header: 'Team Mapping Status' },
      { key: 'teamMappingRuleId', header: 'Team Mapping Rule ID' },
      { key: 'teamMappingRuleType', header: 'Team Mapping Rule Type' },
      { key: 'teamMappingReason', header: 'Team Mapping Reason' },
      { key: 'mappedTeamId', header: 'Mapped Team ID' },
      { key: 'mappedTeamName', header: 'Mapped Team Name' },
      { key: 'mappedProductId', header: 'Mapped Product ID' },
      { key: 'mappedProductName', header: 'Mapped Product Name' },
      ...(DEVEX_METRIC_SPECS as IRawColumnSpec<IDevExNormalizedRow>[]),
    ]),
    [],
  );

  if (loading && !data) {
    return <PageLoading />;
  }

  return (
    <PageContainer variant="wide" className="space-y-6">
      <PageHeader title="Tbl - devex quarterly (normalized view)" subtitle={buildStandardTblSubtitle()} />
      <ErrorBanner message={error} />
      <DataTable
        data={data?.items ?? []}
        columns={columns}
        keyExtractor={getDevExNormalizedRowKey}
        enablePagination
        enableFilters
        emptyMessage={buildStandardEmptyMessage('devex quarterly normalized')}
      />
    </PageContainer>
  );
}
